/**
 * LeadGapIntelligence — premium competitive-intelligence module inspired by
 * Semrush's Keyword Gap but reinterpreted for B2B prospecting.
 *
 * UX skeleton:
 *   1. Header (title + Run / Export)
 *   2. Input panel — main domain + up to 4 competitors + analysis type +
 *      market + period
 *   3. 6 KPI cards (animated counters)
 *   4. Overlap visualization (custom SVG Venn-like map with 6 zones:
 *      Shared, Missing, Weak, Strong, Unique, Untapped). Clicking a zone
 *      filters the table.
 *   5. Smart sortable table with sticky header + badges + per-row
 *      "Generate Outreach" CTA
 *   6. Right panel — AI insights (5 senior-growth-analyst bullets)
 *   7. Operational CTAs at the footer
 *
 * Currently runs on rich mock data — the public/integration_playbook layer
 * isn't wired yet (Phase 2 will plug into SEMrush / Ahrefs / DataforSEO).
 */
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Target, Play, Download, X, Plus, Sparkles, TrendingUp, Zap, Compass,
  Crown, Search as SearchIcon, ArrowUpDown, Filter as FilterIcon,
  Mail, Linkedin, Users, ListPlus, FileText, ChevronDown, Loader2,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ────────────────────────────────────────────────────────────────────── */
/*  Mock data factories                                                   */
/* ────────────────────────────────────────────────────────────────────── */

const ANALYSIS_TYPES = [
  { id: "seo",        label: "SEO Keywords" },
  { id: "paid",       label: "Paid Keywords" },
  { id: "offer",      label: "Offer Positioning" },
  { id: "audience",   label: "Audience / ICP" },
  { id: "content",    label: "Content Topics" },
  { id: "channels",   label: "Acquisition Channels" },
  { id: "outbound",   label: "Prospecting Opportunities" },
];

const MARKETS = [
  { id: "br", label: "Brasil" },
  { id: "us", label: "United States" },
  { id: "global", label: "Global" },
  { id: "latam", label: "LATAM" },
  { id: "eu", label: "Europe" },
];

const PERIODS = [
  { id: "7d",  label: "Últimos 7 dias" },
  { id: "30d", label: "Últimos 30 dias" },
  { id: "90d", label: "Últimos 90 dias" },
  { id: "180d", label: "Últimos 180 dias" },
];

const ZONES = [
  { id: "shared",   label: "Shared",   color: "#64748B", desc: "Vocês competem por esses termos" },
  { id: "missing",  label: "Missing",  color: "#EF4444", desc: "Eles têm, você não" },
  { id: "weak",     label: "Weak",     color: "#F59E0B", desc: "Você compete mal" },
  { id: "strong",   label: "Strong",   color: "#10B981", desc: "Você lidera" },
  { id: "unique",   label: "Unique",   color: "#8B5CF6", desc: "Apenas você" },
  { id: "untapped", label: "Untapped", color: "#06B6D4", desc: "Ninguém explora" },
];

// Realistic-looking mock rows. The Lead Gap engine will replace this with
// data from SEMrush / Ahrefs / DataforSEO in Phase 2.
// `cpc_usd` = average Google Ads cost-per-click in USD for the keyword —
// invaluable for prioritising paid spend and computing potential CAC.
const MOCK_ROWS = [
  { id: 1, item: "roofing miami best contractor", zone: "missing", main: 0,  c1: 8,  c2: 12, c3: 4,  c4: 0,  volume: 2400, difficulty: 38, cpc_usd: 12.40, intent: "commercial", priority: "high",   suggestion: "Criar landing page de SEO local para Miami",                action: "SEO" },
  { id: 2, item: "hvac repair san diego",         zone: "weak",    main: 19, c1: 3,  c2: 5,  c3: 7,  c4: 9,  volume: 1900, difficulty: 42, cpc_usd: 9.80,  intent: "transactional", priority: "high", suggestion: "Reescrever página para responder dúvida 'cost'",            action: "SEO" },
  { id: 3, item: "lead generation b2b agency",    zone: "shared",  main: 12, c1: 9,  c2: 15, c3: 11, c4: 7,  volume: 5400, difficulty: 64, cpc_usd: 24.10, intent: "commercial", priority: "medium", suggestion: "Acelerar autoridade com guest posts",                       action: "Content" },
  { id: 4, item: "ai prospecting tool",           zone: "strong",  main: 3,  c1: 19, c2: 28, c3: 24, c4: 31, volume: 880,  difficulty: 28, cpc_usd: 18.70, intent: "transactional", priority: "high", suggestion: "Lançar campanha paid + comparativo",                       action: "PPC" },
  { id: 5, item: "construction lead generation",  zone: "missing", main: 0,  c1: 14, c2: 7,  c3: 22, c4: 3,  volume: 1200, difficulty: 35, cpc_usd: 15.20, intent: "commercial", priority: "high",   suggestion: "Outbound para 200 construtoras + landing dedicada",         action: "Outbound" },
  { id: 6, item: "saas crm para SDR",             zone: "unique",  main: 1,  c1: 0,  c2: 0,  c3: 0,  c4: 0,  volume: 320,  difficulty: 12, cpc_usd: 7.10,  intent: "transactional", priority: "high", suggestion: "Dobrar conteúdo e capturar termo antes que escale",          action: "Content" },
  { id: 7, item: "waterproofing commercial",      zone: "weak",    main: 4,  c1: 11, c2: 8,  c3: 9,  c4: 6,  volume: 720,  difficulty: 31, cpc_usd: 11.30, intent: "commercial", priority: "medium", suggestion: "Criar case studies de impermeabilização",                   action: "Content" },
  { id: 8, item: "outreach automation tool",      zone: "shared",  main: 7,  c1: 12, c2: 9,  c3: 8,  c4: 10, volume: 2100, difficulty: 55, cpc_usd: 21.50, intent: "transactional", priority: "medium", suggestion: "A/B testar headline focado em reply rate",              action: "PPC" },
  { id: 9, item: "ICP discovery framework",       zone: "untapped", main: 0, c1: 0,  c2: 0,  c3: 1,  c4: 0,  volume: 110,  difficulty: 9,  cpc_usd: 3.40,  intent: "informational", priority: "low", suggestion: "Publicar template + capturar como lead magnet",              action: "Content" },
  { id: 10, item: "sales intelligence platform",  zone: "shared",  main: 6,  c1: 17, c2: 11, c3: 13, c4: 9,  volume: 3300, difficulty: 71, cpc_usd: 32.80, intent: "commercial", priority: "low",    suggestion: "Pivotar para long-tail mais específico",                    action: "SEO" },
  { id: 11, item: "concrete contractor texas",    zone: "missing", main: 0,  c1: 6,  c2: 4,  c3: 2,  c4: 5,  volume: 1480, difficulty: 29, cpc_usd: 8.60,  intent: "transactional", priority: "high", suggestion: "Outbound + página por estado/cidade",                       action: "Outbound" },
  { id: 12, item: "ai cold email writer",         zone: "strong",  main: 22, c1: 9,  c2: 4,  c3: 11, c4: 7,  volume: 1600, difficulty: 33, cpc_usd: 14.20, intent: "transactional", priority: "high", suggestion: "Dobrar paid + pegar share-of-voice",                        action: "PPC" },
];

/* ────────────────────────────────────────────────────────────────────── */
/*  Helpers                                                               */
/* ────────────────────────────────────────────────────────────────────── */

const PRIORITY_BADGE = {
  high:   { bg: "rgba(239,68,68,0.12)",  fg: "#EF4444", label: "Alta" },
  medium: { bg: "rgba(245,158,11,0.14)", fg: "#F59E0B", label: "Média" },
  low:    { bg: "rgba(100,116,139,0.18)", fg: "#64748B", label: "Baixa" },
};

const INTENT_BADGE = {
  commercial:    { bg: "rgba(99,102,241,0.15)",  fg: "#6366F1", label: "Comercial" },
  transactional: { bg: "rgba(16,185,129,0.16)",  fg: "#10B981", label: "Transacional" },
  informational: { bg: "rgba(14,165,233,0.16)",  fg: "#0EA5E9", label: "Informacional" },
};

function genInsights(mainDomain, competitors, analysisType, rows) {
  // Mock insights — Phase 2 will swap this for a GPT-4o call on the actual
  // result rows. Format: array of {tone, text}.
  const compName = competitors[0] || "concorrente A";
  const t = ANALYSIS_TYPES.find((x) => x.id === analysisType)?.label?.toLowerCase() || "análise";
  const main = mainDomain || "sua empresa";
  return [
    { tone: "warn",
      text: `Seus concorrentes dominam termos de fundo de funil em ${t} — você está ausente em ${rows.filter((r) => r.zone === "missing").length} keywords críticas.` },
    { tone: "opp",
      text: `Existe oportunidade de outbound massivo para construtoras e empresas de roofing — 3 termos com volume médio 1.7k/mês sem cobertura sua.` },
    { tone: "warn",
      text: `Seu posicionamento está fraco em "waterproofing commercial" — ${compName} captura 75% do tráfego deste termo.` },
    { tone: "good",
      text: `${main} lidera em "ai cold email writer" (volume 1.6k) — vale dobrar paid budget para travar o share-of-voice antes que escalem.` },
    { tone: "opp",
      text: `Crie campanha LinkedIn focada em SDRs de SaaS B2B usando o ângulo "ICP discovery framework" — ainda não explorado por ninguém na sua categoria.` },
  ];
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Component                                                             */
/* ────────────────────────────────────────────────────────────────────── */

export default function LeadGapIntelligence() {
  const [mainDomain, setMainDomain] = useState("agentelead.pro");
  const [competitors, setCompetitors] = useState(["hubspot.com", "apollo.io", "salesloft.com", ""]);
  const [analysis, setAnalysis] = useState("seo");
  const [market, setMarket] = useState("br");
  const [period, setPeriod] = useState("90d");
  const [filterZone, setFilterZone] = useState(null);
  const [sortKey, setSortKey] = useState("volume");
  const [sortDir, setSortDir] = useState("desc");
  const [ran, setRan] = useState(true);   // mock starts already populated
  const [running, setRunning] = useState(false);
  // CTA execution state — keyed by action so spinners are independent.
  const [ctaRunning, setCtaRunning] = useState({});

  /** Hit the real backend dispatcher for one of the 5 operational CTAs. */
  const runCta = async (action) => {
    setCtaRunning((s) => ({ ...s, [action]: true }));
    try {
      const { data } = await axios.post(`${API}/lead-gap/action`, {
        action,
        main_domain: mainDomain,
        competitors: competitors.filter(Boolean),
        analysis,
        rows: rows.map((r) => ({
          item: r.item, zone: r.zone, priority: r.priority, intent: r.intent,
          suggestion: r.suggestion, volume: r.volume,
        })),
      });
      // Per-action UX feedback.
      if (action === "lead_list") {
        toast.success(`✔ ${data.created} leads criados na base`);
      } else if (action === "cold_email") {
        toast.success(`✔ Campanha "${data.subject?.slice(0, 40) || "Cold email"}" criada`);
      } else if (action === "linkedin") {
        toast.success("✔ Cadência LinkedIn de 3 mensagens criada");
      } else if (action === "export_crm" && data.csv) {
        // Trigger immediate download of the CSV that comes inline.
        const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = data.filename || "lead-gap.csv"; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success(`✔ ${data.rows} linhas exportadas`);
      } else if (action === "save_campaign") {
        toast.success("✔ Snapshot salvo — pode replay depois");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Action failed");
    } finally {
      setCtaRunning((s) => ({ ...s, [action]: false }));
    }
  };

  const updCompetitor = (i, val) => {
    setCompetitors((c) => c.map((x, idx) => (idx === i ? val : x)));
  };

  // KPI numbers derived from the mock rows.
  const kpis = useMemo(() => {
    const missing = MOCK_ROWS.filter((r) => r.zone === "missing").length;
    const untapped = MOCK_ROWS.filter((r) => r.zone === "untapped").length;
    const compUnique = MOCK_ROWS.filter((r) => r.zone === "missing").length;
    const quickWins = MOCK_ROWS.filter((r) => r.priority === "high" && r.difficulty <= 35).length;
    const bad = MOCK_ROWS.filter((r) => r.zone === "missing" || r.zone === "weak").length;
    const score = Math.round(100 - (bad / MOCK_ROWS.length) * 100);
    // Average paid CPC across all rows — gives an at-a-glance estimate of
    // how expensive this competitive set is for paid acquisition.
    const avgCpc = MOCK_ROWS.reduce((s, r) => s + (r.cpc_usd || 0), 0) / MOCK_ROWS.length;
    return [
      { label: "Oportunidades totais",       value: MOCK_ROWS.length,                 tone: "neutral", icon: Target },
      { label: "Gaps críticos",              value: missing,                          tone: "danger",  icon: Zap },
      { label: "Mercados não explorados",    value: untapped,                         tone: "info",    icon: Compass },
      { label: "Quick wins",                 value: quickWins,                        tone: "good",    icon: TrendingUp },
      { label: "CPC médio",                  value: `$${avgCpc.toFixed(2)}`,          tone: "warn",    icon: Crown },
      { label: "Competitividade",            value: `${score}%`,                      tone: "good",    icon: Sparkles },
    ];
  }, []);

  const rows = useMemo(() => {
    let r = MOCK_ROWS;
    if (filterZone) r = r.filter((x) => x.zone === filterZone);
    return [...r].sort((a, b) => {
      const A = a[sortKey], B = b[sortKey];
      if (typeof A === "number") return sortDir === "asc" ? A - B : B - A;
      return sortDir === "asc" ? String(A).localeCompare(String(B)) : String(B).localeCompare(String(A));
    });
  }, [filterZone, sortKey, sortDir]);

  const insights = useMemo(
    () => genInsights(mainDomain, competitors.filter(Boolean), analysis, MOCK_ROWS),
    [mainDomain, competitors, analysis]
  );

  const run = () => {
    setRunning(true);
    setTimeout(() => {
      setRunning(false);
      setRan(true);
      toast.success("Análise concluída — 12 oportunidades encontradas");
    }, 900);
  };

  const exportCsv = () => {
    const header = ["keyword", "zone", "main", "c1", "c2", "c3", "c4", "volume", "cpc_usd", "difficulty", "intent", "priority", "suggestion", "action"];
    const lines = [header.join(","), ...rows.map((r) => header.map((h) => `"${String(r[h] ?? r.item ?? "").replaceAll('"', '""')}"`).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `lead-gap-${Date.now()}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("CSV exportado");
  };

  const sortBy = (key) => {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)] flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex items-center justify-between px-6 py-3 border-b border-[var(--al-border)] bg-[var(--al-card-bg)] shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Logo />
            <div className="min-w-0">
              <h1 className="text-[17px] font-bold leading-tight flex items-center gap-2">
                <Target className="w-4 h-4 text-[#C9A24E]" />
                Lead Gap Intelligence
              </h1>
              <p className="text-[11.5px] text-[var(--al-text-muted)] mt-0.5">
                Compare sua empresa com até 4 concorrentes e descubra lacunas estratégicas.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ClaireHelpButton testid="leadgap"
              question="Como uso o Lead Gap Intelligence — comparar concorrentes e priorizar keywords?" />
            <LangToggle />
            <ThemeToggle />
            <button onClick={exportCsv}
              data-testid="leadgap-export"
              className="h-8 px-3 rounded-lg border border-[var(--al-border)] text-[11.5px] font-semibold hover:bg-[var(--al-row-alt)] inline-flex items-center gap-1.5">
              <Download className="w-3 h-3" /> Export Report
            </button>
            <button onClick={run} disabled={running}
              data-testid="leadgap-run"
              className="h-8 px-4 rounded-lg bg-[#C9A24E] hover:bg-[#dcb670] text-[#1A0E36] text-[11.5px] font-bold inline-flex items-center gap-1.5 transition-colors disabled:opacity-60">
              <Play className="w-3 h-3" /> Run Analysis
            </button>
          </div>
        </header>

        {/* Main scroll area */}
        <main className="flex-1 px-6 py-5 space-y-5">
          {/* Input panel */}
          <section className="al-card p-4">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_auto] gap-4">
              {/* Main domain */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1.5">
                  Empresa principal
                </label>
                <div className="relative">
                  <Target className="w-3.5 h-3.5 text-[#C9A24E] absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input value={mainDomain} onChange={(e) => setMainDomain(e.target.value)}
                    data-testid="leadgap-main-domain"
                    placeholder="agentelead.pro ou nome da empresa"
                    className="w-full h-9 pl-8 pr-3 rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)] text-[12.5px] focus:border-[#C9A24E] outline-none" />
                </div>
              </div>
              {/* Competitors */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1.5">
                  Concorrentes (até 4)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {competitors.map((c, i) => (
                    <div key={i} className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[9px] font-bold text-[var(--al-text-muted)] tabular-nums">
                        C{i + 1}
                      </span>
                      <input value={c} onChange={(e) => updCompetitor(i, e.target.value)}
                        data-testid={`leadgap-competitor-${i + 1}`}
                        placeholder={i === 0 ? "hubspot.com" : "opcional"}
                        className="w-full h-9 pl-8 pr-3 rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)] text-[12.5px] focus:border-[#C9A24E] outline-none" />
                      {c && (
                        <button onClick={() => updCompetitor(i, "")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--al-text-muted)] hover:text-rose-400">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* Filters */}
              <div className="flex flex-col gap-2 min-w-[220px]">
                <Dropdown value={analysis} onChange={setAnalysis} options={ANALYSIS_TYPES} label="Tipo de análise" testid="leadgap-analysis" />
                <div className="grid grid-cols-2 gap-2">
                  <Dropdown value={market}   onChange={setMarket}   options={MARKETS} label="Mercado" testid="leadgap-market" />
                  <Dropdown value={period}   onChange={setPeriod}   options={PERIODS} label="Período" testid="leadgap-period" />
                </div>
              </div>
            </div>
          </section>

          {/* KPIs */}
          <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
          </section>

          {/* Overlap visualization */}
          <section className="al-card p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="text-[13px] font-bold">Mapa de sobreposição</h3>
                <p className="text-[11px] text-[var(--al-text-muted)] mt-0.5">Clique em uma zona para filtrar a tabela abaixo.</p>
              </div>
              {filterZone && (
                <button onClick={() => setFilterZone(null)}
                  data-testid="leadgap-clear-zone"
                  className="text-[10.5px] font-bold uppercase tracking-wider text-[#C9A24E] hover:underline">
                  Limpar filtro ({filterZone})
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
              {ZONES.map((z) => {
                const count = MOCK_ROWS.filter((r) => r.zone === z.id).length;
                const active = filterZone === z.id;
                return (
                  <button key={z.id}
                    onClick={() => setFilterZone(active ? null : z.id)}
                    data-testid={`leadgap-zone-${z.id}`}
                    className={`group text-left rounded-xl p-3 border transition-all ${active ? "scale-[1.02] shadow-md" : "hover:scale-[1.01]"}`}
                    style={{
                      borderColor: active ? z.color : "var(--al-border)",
                      background: active ? `${z.color}18` : "var(--al-card-bg)",
                    }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider"
                            style={{ color: z.color }}>{z.label}</span>
                      <span className="text-[18px] font-extrabold tabular-nums">{count}</span>
                    </div>
                    <div className="text-[10.5px] text-[var(--al-text-muted)] leading-snug">{z.desc}</div>
                    <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: "var(--al-border)" }}>
                      <div className="h-full rounded-full transition-all" style={{ background: z.color, width: `${(count / MOCK_ROWS.length) * 100}%` }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Table + AI Insights */}
          <section className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
            {/* Table */}
            <div className="al-card overflow-hidden flex flex-col" style={{ minHeight: 360 }}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--al-border)]">
                <div className="flex items-center gap-2">
                  <FilterIcon className="w-3.5 h-3.5 text-[var(--al-text-muted)]" />
                  <h3 className="text-[13px] font-bold">Detalhamento por oportunidade</h3>
                  <span className="text-[10.5px] text-[var(--al-text-muted)]">{rows.length} de {MOCK_ROWS.length}</span>
                </div>
              </div>
              <div className="overflow-auto" data-testid="leadgap-table-wrap">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-[var(--al-card-bg)] z-10">
                    <tr className="border-b border-[var(--al-border)]">
                      <SortableTh label="Keyword / Topic" k="item"       sortKey={sortKey} sortDir={sortDir} onSort={sortBy} />
                      <SortableTh label="Volume"          k="volume"     sortKey={sortKey} sortDir={sortDir} onSort={sortBy} align="right" />
                      <SortableTh label="CPC (US$)"       k="cpc_usd"    sortKey={sortKey} sortDir={sortDir} onSort={sortBy} align="right" />
                      <SortableTh label="KD"              k="difficulty" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} align="right" />
                      <Th label="Intent" />
                      <Th label="Priority" />
                      <Th label="Sua posição" align="right" />
                      <Th label="Concorrentes (avg)" align="right" />
                      <Th label="Sugestão" />
                      <Th label="" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const compAvg = ((r.c1 + r.c2 + r.c3 + r.c4) / 4).toFixed(0);
                      const intentB = INTENT_BADGE[r.intent];
                      const prioB = PRIORITY_BADGE[r.priority];
                      const zoneColor = ZONES.find((z) => z.id === r.zone)?.color || "#64748B";
                      return (
                        <tr key={r.id} data-testid={`leadgap-row-${r.id}`}
                            className="border-b border-[var(--al-border)] hover:bg-[var(--al-row-alt)] transition-colors">
                          <td className="px-3 py-2.5 max-w-[260px]">
                            <div className="flex items-center gap-2">
                              <span className="w-1 h-4 rounded-full shrink-0" style={{ background: zoneColor }} />
                              <span className="font-semibold truncate" title={r.item}>{r.item}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{r.volume.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">
                            <CpcChip cpc={r.cpc_usd} />
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">
                            <DifficultyChip kd={r.difficulty} />
                          </td>
                          <td className="px-3 py-2.5">
                            <Badge bg={intentB.bg} fg={intentB.fg} label={intentB.label} />
                          </td>
                          <td className="px-3 py-2.5">
                            <Badge bg={prioB.bg} fg={prioB.fg} label={prioB.label} />
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-semibold"
                              style={{ color: r.main === 0 ? "#EF4444" : "inherit" }}>
                            {r.main || "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-[var(--al-text-muted)]">
                            {compAvg}
                          </td>
                          <td className="px-3 py-2.5 text-[11.5px] text-[var(--al-text-muted)] max-w-[260px] truncate" title={r.suggestion}>
                            {r.suggestion}
                          </td>
                          <td className="px-3 py-2.5">
                            <button onClick={async () => {
                              setCtaRunning((s) => ({ ...s, [`row-${r.id}`]: true }));
                              try {
                                const { data } = await axios.post(`${API}/lead-gap/action`, {
                                  action: "cold_email",
                                  main_domain: mainDomain,
                                  competitors: competitors.filter(Boolean),
                                  analysis,
                                  rows: [{ item: r.item, zone: r.zone, priority: r.priority,
                                           intent: r.intent, suggestion: r.suggestion, volume: r.volume }],
                                });
                                toast.success(`Cold email "${data.subject?.slice(0, 36)}…" criado`);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || err.message);
                              } finally {
                                setCtaRunning((s) => ({ ...s, [`row-${r.id}`]: false }));
                              }
                            }}
                              disabled={ctaRunning[`row-${r.id}`]}
                              data-testid={`leadgap-outreach-${r.id}`}
                              className="text-[10.5px] font-bold px-2.5 py-1 rounded-md border border-[#C9A24E]/40 text-[#C9A24E] hover:bg-[#C9A24E]/10 inline-flex items-center gap-1 disabled:opacity-60">
                              {ctaRunning[`row-${r.id}`]
                                ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                : <Sparkles className="w-2.5 h-2.5" />}
                              Outreach
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr><td colSpan={10} className="px-3 py-10 text-center text-[var(--al-text-muted)]">
                        Nenhum item nesta zona — limpe o filtro para ver tudo.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* AI Insights */}
            <aside className="al-card p-4 space-y-3" data-testid="leadgap-ai-panel">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-500 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <h3 className="text-[13px] font-bold leading-tight">Insights por IA</h3>
                  <p className="text-[10.5px] text-[var(--al-text-muted)]">Análise sênior de growth</p>
                </div>
              </div>
              <div className="space-y-2">
                {insights.map((ins, i) => {
                  const accent = ins.tone === "warn" ? "#EF4444"
                              : ins.tone === "good" ? "#10B981"
                              : "#8B5CF6";
                  return (
                    <div key={i} className="text-[11.5px] leading-snug p-2.5 rounded-lg border"
                         style={{ borderColor: "var(--al-border)", borderLeft: `3px solid ${accent}` }}>
                      {ins.text}
                    </div>
                  );
                })}
              </div>
              <div className="pt-2 border-t border-[var(--al-border)] grid grid-cols-1 gap-1.5">
                <CtaBtn icon={ListPlus}  label="Generate Lead List"       loading={ctaRunning.lead_list}     onClick={() => runCta("lead_list")} testid="leadgap-cta-leadlist" />
                <CtaBtn icon={Mail}      label="Build Cold Email Sequence" loading={ctaRunning.cold_email}    onClick={() => runCta("cold_email")} testid="leadgap-cta-coldemail" />
                <CtaBtn icon={Linkedin}  label="Create LinkedIn Outreach"  loading={ctaRunning.linkedin}      onClick={() => runCta("linkedin")} testid="leadgap-cta-linkedin" />
                <CtaBtn icon={Users}     label="Export to CRM"             loading={ctaRunning.export_crm}    onClick={() => runCta("export_crm")} testid="leadgap-cta-export" />
                <CtaBtn icon={FileText}  label="Save as Campaign"          loading={ctaRunning.save_campaign} onClick={() => runCta("save_campaign")} testid="leadgap-cta-save" />
              </div>
            </aside>
          </section>
        </main>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Sub-components                                                        */
/* ────────────────────────────────────────────────────────────────────── */

function KpiCard({ label, value, tone, icon: Icon }) {
  const toneColor = tone === "danger" ? "#EF4444"
                  : tone === "warn"   ? "#F59E0B"
                  : tone === "good"   ? "#10B981"
                  : tone === "info"   ? "#06B6D4"
                  : "#C9A24E";
  return (
    <div className="al-card p-3.5 flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
           style={{ background: `${toneColor}18`, color: toneColor }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[var(--al-text-muted)] font-bold mb-0.5">{label}</div>
        <div className="text-[22px] font-extrabold tabular-nums leading-none">{value}</div>
      </div>
    </div>
  );
}

function Dropdown({ value, onChange, options, label, testid }) {
  return (
    <label className="block">
      {label && (
        <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1.5">
          {label}
        </div>
      )}
      <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full h-9 pl-3 pr-8 rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)] text-[12.5px] focus:border-[#C9A24E] outline-none appearance-none cursor-pointer">
          {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--al-text-muted)] pointer-events-none" />
      </div>
    </label>
  );
}

function Th({ label, align = "left" }) {
  return (
    <th className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] text-${align}`}>
      {label}
    </th>
  );
}

function SortableTh({ label, k, sortKey, sortDir, onSort, align = "left" }) {
  const active = sortKey === k;
  return (
    <th className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-${align}`}>
      <button onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 ${active ? "text-[#C9A24E]" : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"}`}>
        {label}
        <ArrowUpDown className="w-2.5 h-2.5" />
        {active && <span className="text-[9px]">{sortDir === "asc" ? "↑" : "↓"}</span>}
      </button>
    </th>
  );
}

function Badge({ bg, fg, label }) {
  return (
    <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider"
          style={{ background: bg, color: fg }}>
      {label}
    </span>
  );
}

function DifficultyChip({ kd }) {
  const color = kd >= 60 ? "#EF4444" : kd >= 35 ? "#F59E0B" : "#10B981";
  return (
    <span className="inline-flex items-center gap-1 font-semibold tabular-nums" style={{ color }}>
      {kd}
    </span>
  );
}

function CpcChip({ cpc }) {
  // CPC heatmap: red = expensive (≥$15) → green = cheap (<$5). Helps the
  // user spot quick-win paid keywords (low CPC + decent volume).
  const color = cpc >= 20 ? "#EF4444" : cpc >= 10 ? "#F59E0B" : "#10B981";
  const fmt = cpc != null ? `$${cpc.toFixed(2)}` : "—";
  return (
    <span className="inline-flex items-center gap-1 font-semibold tabular-nums" style={{ color }}>
      {fmt}
    </span>
  );
}

function CtaBtn({ icon: Icon, label, onClick, loading = false, testid }) {
  return (
    <button onClick={onClick} disabled={loading}
      data-testid={testid}
      className="w-full h-8 px-2.5 rounded-lg border border-[var(--al-border)] text-[11px] font-semibold hover:bg-[var(--al-row-alt)] hover:border-[#C9A24E]/40 inline-flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-wait">
      {loading
        ? <Loader2 className="w-3 h-3 text-[#C9A24E] animate-spin" />
        : <Icon className="w-3 h-3 text-[#C9A24E]" />}
      {label}
    </button>
  );
}
